import { GridLayout } from '@findy-code/yuffie/ui/grid-layout';
import { Stack } from '@findy-code/yuffie/ui/stack';
import type { ReactNode } from 'react';

import type { TabItem } from '../../../common';
import { CompanyImages, DetailCompanyTitle, Tabs } from '../../../common';
import type { FetchCompanyDetailCompanyFragment } from '../../graphql';
import styles from './company-detail.component.module.scss';
import type { SectionIds } from './company-detail.presenter';
import { SectionScrollObserverProvider } from './company-detail.presenter';
import { CompanyDetailBreadcrumbs } from './company-detail-breadcrumbs.component';
import { CompanyDetailEntries } from './company-detail-entries.component';
import { CompanyDetailEvents } from './company-detail-events.component';
import { CompanyDetailGithub } from './company-detail-github.component';
import { CompanyDetailInterviews } from './company-detail-interviews.component';
import { CompanyDetailMembers } from './company-detail-members.component';
import { CompanyDetailPressRelease } from './company-detail-press-release.component';
import { CompanyDetailProducts } from './company-detail-products.component';
import { CompanyDetailRelatedLinks } from './company-detail-related-links.component';
import { CompanyDetailSectionNavigator } from './company-detail-section-navigator.component';
import { CompanyDetailSkills } from './company-detail-skills.component';
import { CompanyDetailSummary } from './company-detail-summary.component';
import { CompanyDetailTable } from './company-detail-table.component';
import { CompanyDetailMissionVision } from './company-detail-vision.component';

const GridMainItem = ({ children }: { children: ReactNode }) => {
  return <div className={styles.row}>{children}</div>;
};

export const CompanyDetailComponent = ({
  company,
  isLogin,
}: {
  company: FetchCompanyDetailCompanyFragment;
  isLogin?: boolean;
}) => {
  const tab_items: TabItem[] = [
    {
      label: '企業情報',
      path: `/companies/${company.id}/`,
      current: true,
    },
    {
      label: '求人一覧',
      path: `/companies/${company.id}/jobs/`,
      current: false,
    },
  ];

  const images = company.images;

  const breadcrumbs = company.name
    ? [
        { name: 'Home', link: isLogin ? '/home' : '/', active: false },
        { name: '企業一覧', link: '/companies/', active: false },
        {
          name: company.name,
          link: `/companies/${company.id}/`,
          active: true,
        },
      ]
    : [];

  const pressReleaseEntries = company.pressReleaseEntries;

  const sectionVisibility: { [key in SectionIds]: boolean } = {
    mission: !!company.profile?.missionTitle,
    vision: !!company.profile?.visionTitle,
    product: company.products.length > 0,
    interview: company.publishedInterviews.length > 0,
    member: company.members.length > 0,
    entries: company.techBlogEntries.length > 0,
    skills: company.selfRecommendedSkills.length > 0,
    'git-hub': !!company.githubAccount,
    'basic-information':
      process.env.NEXT_PUBLIC_FEATURE_UPDATE_COMPANY_SUMMARY !== 'true',
    summary: process.env.NEXT_PUBLIC_FEATURE_UPDATE_COMPANY_SUMMARY === 'true',
    links: company.relatedLinks.length > 0,
    'press-release': pressReleaseEntries.length > 0,
    events: company.publishedPromotionVideos.length > 0,
  };

  return (
    <GridLayout isRowGapDisabled className={styles.gridContainer}>
      <div className={styles.breadCrumbs}>
        <CompanyDetailBreadcrumbs breadcrumbs={breadcrumbs} />
      </div>
      <SectionScrollObserverProvider>
        <div className={styles.main}>
          <aside className={styles.nav}>
            <CompanyDetailSectionNavigator
              sectionVisibility={sectionVisibility}
            />
          </aside>
          <GridMainItem>
            <DetailCompanyTitle
              companyDescription={company.description ?? ''}
              companyImageAlt={company.name}
              companyImageUrl={company.imageUrl}
              companyUrl={company.url}
              employeeCount={company.employeeCount}
              title={company.name ? `${company.name}` : ''}
            />
            <Stack spacing={{ base: 'l', md: 'x4l' }}>
              <div className={styles.fixedTabsWrapper}>
                <Tabs
                  items={tab_items}
                  url={`${process.env.NEXT_PUBLIC_FINDY_CODE_URL_BASE}/companies/${company.id}`}
                />
              </div>

              {images.length > 0 && (
                <CompanyImages
                  companyName={company.name}
                  images={company.images}
                />
              )}
              {company.profile && (
                <>
                  {sectionVisibility.mission && (
                    <CompanyDetailMissionVision
                      content={company.profile.missionContent ?? ''}
                      title={company.profile.missionTitle ?? ''}
                      type="mission"
                    />
                  )}
                  {sectionVisibility.vision && (
                    <CompanyDetailMissionVision
                      content={company.profile.visionContent ?? ''}
                      title={company.profile.visionTitle ?? ''}
                      type="vision"
                    />
                  )}
                </>
              )}
              {sectionVisibility.product && (
                <CompanyDetailProducts products={company.products} />
              )}
              {sectionVisibility.interview && (
                <CompanyDetailInterviews
                  interviews={company.publishedInterviews}
                />
              )}
              {sectionVisibility.events && (
                <CompanyDetailEvents
                  videos={company.publishedPromotionVideos}
                />
              )}
              {sectionVisibility.entries && (
                <CompanyDetailEntries
                  blogUrl={company.techBlogUrl ?? ''}
                  entries={company.techBlogEntries}
                />
              )}
              {sectionVisibility.member && (
                <CompanyDetailMembers
                  companyId={company.id}
                  members={company.members}
                />
              )}
              {sectionVisibility['press-release'] && (
                <CompanyDetailPressRelease entries={pressReleaseEntries} />
              )}
              {process.env.NEXT_PUBLIC_FEATURE_UPDATE_COMPANY_SUMMARY ===
              'true' ? (
                <CompanyDetailSummary
                  description={company.description ?? ''}
                  employeeCount={company.employeeCount}
                  location={company.location ?? ''}
                  name={company.name}
                  url={company.url}
                />
              ) : (
                <CompanyDetailTable
                  companyDescription={company.description}
                  companyEstablishedAt={company.establishedAt}
                  companyLocation={company.location}
                  companyPresident={company.president}
                />
              )}
              {sectionVisibility['git-hub'] && (
                <CompanyDetailGithub
                  companyName={company.name}
                  githubAccount={company.githubAccount}
                  githubMemberCount={company.githubMembersCount}
                  githubRepositories={company.githubRepositories}
                />
              )}
              {sectionVisibility.skills && (
                <CompanyDetailSkills skills={company.selfRecommendedSkills} />
              )}
              {sectionVisibility.links && (
                <CompanyDetailRelatedLinks
                  relatedLinks={company.relatedLinks}
                />
              )}
            </Stack>
          </GridMainItem>
        </div>
      </SectionScrollObserverProvider>
    </GridLayout>
  );
};
